import {
  useState,
  useEffect,
  useReducer,
  useContext,
  useRef,
  useMemo,
} from "react";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

import axios from "axios";
import api from "../../services/api";
import { SocketContext } from "../../context/Socket/SocketContext";


const isSameWhatsAppId = (leftId, rightId) => Number(leftId) === Number(rightId);

const reducer = (state, action) => {
  const getUpdatedAtTime = (updatedAt) => {
    const parsed = Date.parse(updatedAt || "");
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const shouldApplyIncomingUpdate = (current, incoming) => {
    if (!current) return true;
    return (
      getUpdatedAtTime(incoming?.updatedAt) >=
      getUpdatedAtTime(current?.updatedAt)
    );
  };

  const mergeWhatsAppsByRecency = (currentState, incomingState) => {
    const mergedById = new Map(
      currentState.map((session) => [session.id, session]),
    );

    incomingState.forEach((incomingSession) => {
      const currentSession = mergedById.get(incomingSession.id);

      if (shouldApplyIncomingUpdate(currentSession, incomingSession)) {
        mergedById.set(incomingSession.id, {
          ...currentSession,
          ...incomingSession,
        });
      }
    });

    return Array.from(mergedById.values());
  };

  if (action.type === "LOAD_WHATSAPPS") {
    const whatsApps = action.payload;

    return mergeWhatsAppsByRecency(state, whatsApps);
  }

  if (action.type === "UPDATE_WHATSAPPS") {
    const whatsApp = action.payload;
    const whatsAppIndex = state.findIndex((s) => isSameWhatsAppId(s.id, whatsApp.id));

    if (whatsAppIndex !== -1) {
      if (!shouldApplyIncomingUpdate(state[whatsAppIndex], whatsApp)) {
        return [...state];
      }

      state[whatsAppIndex] = { ...state[whatsAppIndex], ...whatsApp };
      return [...state];
    } else {
      return [whatsApp, ...state];
    }
  }

  if (action.type === "UPDATE_SESSION") {
    const whatsApp = action.payload;
      const whatsAppIndex = state.findIndex((s) => isSameWhatsAppId(s.id, whatsApp.id));

    if (whatsAppIndex !== -1) {
      if (!shouldApplyIncomingUpdate(state[whatsAppIndex], whatsApp)) {
        return [...state];
      }

      state[whatsAppIndex].status = whatsApp.status;
      state[whatsAppIndex].updatedAt = whatsApp.updatedAt;
      state[whatsAppIndex].qrcode = whatsApp.qrcode;
      state[whatsAppIndex].retries = whatsApp.retries;
      return [...state];
    } else {
      return [whatsApp, ...state];
    }
  }
  if (action.type === "SET_SESSION_STATUS") {
    const { whatsappId, status } = action.payload;
    const whatsAppIndex = state.findIndex((s) => isSameWhatsAppId(s.id, whatsappId));

    if (whatsAppIndex !== -1) {
      state[whatsAppIndex].status = status;
      state[whatsAppIndex].updatedAt = new Date().toISOString();
      return [...state];
    }

    return [...state];
  }

  if (action.type === "DELETE_WHATSAPPS") {
    const whatsAppId = action.payload;

    const whatsAppIndex = state.findIndex((s) => isSameWhatsAppId(s.id, whatsAppId));
    if (whatsAppIndex !== -1) {
      state.splice(whatsAppIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useWhatsApps = () => {
  const [whatsApps, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(true);
  const [socketIsActive, setSocketIsActive] = useState(false);

  const pollingTimerRef = useRef(null);
  const socketHeartbeatTimerRef = useRef(null);

  const transientStatuses = useMemo(
    () => new Set(["OPENING", "PAIRING", "TIMEOUT"]),
    [],
  );

  const socketManager = useContext(SocketContext);

  const clearPollingTimer = () => {
    if (pollingTimerRef.current) {
      clearInterval(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }
  };

  const keepSocketAsActive = () => {
    setSocketIsActive(true);

    if (socketHeartbeatTimerRef.current) {
      clearTimeout(socketHeartbeatTimerRef.current);
    }

    socketHeartbeatTimerRef.current = setTimeout(() => {
      setSocketIsActive(false);
    }, 12000);
  };

  useEffect(() => {
    setLoading(true);
    const fetchSession = async () => {
      try {
        const { data } = await api.get("/whatsapp/?session=0");
        dispatch({ type: "LOAD_WHATSAPPS", payload: data });
        setLoading(false);
      } catch (err) {
        setLoading(false);

        // Public routes reject without a session (api.js); keep the console clean.
        if (!axios.isCancel(err)) {
          toastError(err);
        }
      }
    };
    fetchSession();
  }, []);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);
    const whatsappEvent = `company-${companyId}-whatsapp`;
    const whatsappSessionEvent = `company-${companyId}-whatsappSession`;

    const handleWhatsAppEvent = (data) => {
      keepSocketAsActive();

      if (data.action === "update") {
        dispatch({ type: "UPDATE_WHATSAPPS", payload: data.whatsapp });
      }
    };

    const handleWhatsAppDeleteEvent = (data) => {
      keepSocketAsActive();

      if (data.action === "delete") {
        dispatch({ type: "DELETE_WHATSAPPS", payload: data.whatsappId });
      }
    };

    const handleWhatsAppSessionEvent = (data) => {
      keepSocketAsActive();

      if (data.reason === "REMOVED_BY_PHONE") {
        toast.warning(
          "O dispositivo foi removido no celular. Gere um novo QR.",
        );
      }

      if (data.reason === "UNAUTHORIZED_401") {
        toast.error("Sessão expirada — necessário gerar novo QR.");
      }

      if (data.reason === "RESTART_POST_PAIRING_515") {
        toast.info(
          "Ajustando sessão após pareamento... aguarde alguns segundos.",
        );
      }

      if (data.session?.status === "PENDING") {
        console.log(
          "[frontend] Sessão marcada como PENDING — aguardando backend.",
        );
      }

      if (data.action === "update") {
        dispatch({ type: "UPDATE_SESSION", payload: data.session });
      }
    };

    socket.on(whatsappEvent, handleWhatsAppEvent);
    socket.on(whatsappEvent, handleWhatsAppDeleteEvent);
    socket.on(whatsappSessionEvent, handleWhatsAppSessionEvent);

    return () => {
      socket.off(whatsappEvent, handleWhatsAppEvent);
      socket.off(whatsappEvent, handleWhatsAppDeleteEvent);
      socket.off(whatsappSessionEvent, handleWhatsAppSessionEvent);

      clearPollingTimer();

      if (socketHeartbeatTimerRef.current) {
        clearTimeout(socketHeartbeatTimerRef.current);
        socketHeartbeatTimerRef.current = null;
      }

      socket.disconnect();
    };
  }, [socketManager]);

  useEffect(() => {
    const hasTransientSession = whatsApps.some((session) =>
      transientStatuses.has(session.status),
    );

    if (!hasTransientSession || socketIsActive) {
      clearPollingTimer();
      return;
    }

    const runPolling = async () => {
      try {
        const { data } = await api.get("/whatsapp/?session=0");
        dispatch({ type: "LOAD_WHATSAPPS", payload: data });
      } catch (err) {
        toastError(err);
      }
    };

    runPolling();
    pollingTimerRef.current = setInterval(runPolling, 7000);

    return () => {
      clearPollingTimer();
    };
  }, [socketIsActive, transientStatuses, whatsApps]);

  useEffect(
    () => () => {
      clearPollingTimer();

      if (socketHeartbeatTimerRef.current) {
        clearTimeout(socketHeartbeatTimerRef.current);
        socketHeartbeatTimerRef.current = null;
      }
    },
    [],
  );

  const setSessionStatus = (whatsappId, status) => {
    dispatch({ type: "SET_SESSION_STATUS", payload: { whatsappId, status } });
  };

  return { whatsApps, loading, setSessionStatus };
};

export default useWhatsApps;
